import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { useNavigate } from "react-router-dom";
import adminApiKeyAxiosInstance from "utils/adminApiKeyAxiosInstance";
import { getUrl } from "utils/getURL";
import { AdminAPIResource, CustomerSource, KycStatusGroup } from "constants/adminApi";
import { NAME_MATCH_TESTER_PATH } from "constants/routesVariant";
import { ESTABLISHMENT_OPTIONS } from "constants/establishmentTypeOptions";
import {
  formatKycStatusLabel,
  NON_VERIFIED_KYC_STATUS_OPTIONS,
} from "constants/nonVerifiedKycStatusOptions";
import { PagedCustomerKycList, ReportDownloadResponse } from "types/admin";
import AdminPageHeader from "./components/AdminPageHeader";
import { formatDate, formatEstablishmentType, kycStatusChipColor } from "./components/adminKycVerificationUtils";
import { NameMatchedIcon } from "./components/kycDocumentDetailTags";

const PAGE_SIZE = 20;

const BRAND_BLUE = "#007CBF";

const filterTabsSx = {
  minHeight: 36,
  "& .MuiTabs-flexContainer": { gap: 0.5 },
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": {
    minHeight: 32,
    py: 0.5,
    px: 1.75,
    borderRadius: 1.5,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    color: "text.secondary",
    border: "1px solid",
    borderColor: "divider",
    "&.Mui-selected": {
      color: BRAND_BLUE,
      bgcolor: "rgba(0, 124, 191, 0.08)",
      borderColor: "rgba(0, 124, 191, 0.4)",
    },
  },
} as const;

type ViewMode = "verified" | "nonVerified" | "onHold";
type LastUpdatedSort = "ASC" | "DESC";

const VIEW_MODE_TO_STATUS_GROUP: Record<ViewMode, KycStatusGroup> = {
  verified: "VERIFIED",
  nonVerified: "NON_VERIFIED",
  onHold: "ON_HOLD",
};

function VerifiedKycUsersPage() {
  const navigate = useNavigate();
  const [customerSource, setCustomerSource] = useState<CustomerSource>("BRP");
  const [viewMode, setViewMode] = useState<ViewMode>("verified");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [establishmentTypeFilter, setEstablishmentTypeFilter] = useState("");
  const [kycStatusFilter, setKycStatusFilter] = useState("");
  const [lastUpdatedSort, setLastUpdatedSort] = useState<LastUpdatedSort>("DESC");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PagedCustomerKycList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { paramName: string; paramValue: string | number }[] = [
        { paramName: "customerSource", paramValue: customerSource },
        { paramName: "kycStatusGroup", paramValue: VIEW_MODE_TO_STATUS_GROUP[viewMode] },
        { paramName: "page", paramValue: page },
        { paramName: "size", paramValue: PAGE_SIZE },
      ];

      if (search) {
        params.push({ paramName: "search", paramValue: search });
      }

      if (establishmentTypeFilter) {
        params.push({ paramName: "establishmentType", paramValue: establishmentTypeFilter });
      }

      if (viewMode === "nonVerified" && kycStatusFilter) {
        params.push({ paramName: "kycStatus", paramValue: kycStatusFilter });
      }

      params.push({ paramName: "lastUpdatedSort", paramValue: lastUpdatedSort });

      const url = getUrl(AdminAPIResource.listCustomers, undefined, params);
      const response = await adminApiKeyAxiosInstance.get<PagedCustomerKycList>(url);
      setData(response.data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load customers";
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerSource, establishmentTypeFilter, kycStatusFilter, lastUpdatedSort, page, search, viewMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSourceChange = (_: React.SyntheticEvent, value: CustomerSource) => {
    setCustomerSource(value);
    setPage(0);
  };

  const handleViewModeChange = (_: React.SyntheticEvent, value: ViewMode) => {
    setViewMode(value);
    if (value !== "nonVerified") {
      setKycStatusFilter("");
    }
    setPage(0);
  };

  const handleEstablishmentTypeFilterChange = (value: string) => {
    setEstablishmentTypeFilter(value);
    setPage(0);
  };

  const handleKycStatusFilterChange = (value: string) => {
    setKycStatusFilter(value);
    setPage(0);
  };

  const handleLastUpdatedSort = () => {
    setLastUpdatedSort((current) => (current === "DESC" ? "ASC" : "DESC"));
    setPage(0);
  };

  const handleRowClick = (customerId: string, source: CustomerSource) => {
    if (viewMode === "verified") {
      navigate(`/verified-kyc-users/${source}/${customerId}?kycStatusGroup=VERIFIED`);
      return;
    }
    const group = VIEW_MODE_TO_STATUS_GROUP[viewMode];
    navigate(`/verified-kyc-users/${source}/${customerId}/verification?kycStatusGroup=${group}`);
  };

  const handleExcelDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const url = getUrl(AdminAPIResource.reportDownload, [
        { variableName: "kycStatusGroup", variableValue: VIEW_MODE_TO_STATUS_GROUP[viewMode] },
        { variableName: "customerSource", variableValue: customerSource },
      ]);
      const response = await adminApiKeyAxiosInstance.get<ReportDownloadResponse>(url);
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank", "noopener,noreferrer");
      } else {
        setDownloadError(response.data.error || "Report not available");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to download report";
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  };

  const emptyMessages: Record<ViewMode, string> = {
    verified: "No verified customers found",
    nonVerified: "No non-verified customers found",
    onHold: "No on-hold customers found",
  };

  const subtitles: Record<ViewMode, string> = {
    verified: "Verified retailer records — KYC complete",
    nonVerified: "Non-verified retailers — pending verification review",
    onHold: "Verified retailers on hold — name mismatch review",
  };

  const statLabels: Record<ViewMode, string> = {
    verified: "Verified customers",
    nonVerified: "Non-verified customers",
    onHold: "On-hold customers",
  };

  const showReviewColumns = viewMode !== "verified";
  const showNameMatchedColumn = viewMode === "nonVerified";
  const colSpan = 8 + (showNameMatchedColumn ? 1 : 0) + (showReviewColumns ? 2 : 0);
  const totalRecords = data?.totalElements ?? 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f4f8" }}>
      <AdminPageHeader
        title="KYC Admin Console"
        subtitle={subtitles[viewMode]}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Compare PAN and bank names with the production matcher">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CompareArrowsOutlinedIcon />}
                onClick={() => navigate(NAME_MATCH_TESTER_PATH)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Name match
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={downloading}
              onClick={handleExcelDownload}
              sx={{ bgcolor: "#007CBF", "&:hover": { bgcolor: "#006aa3" } }}
            >
              {downloading ? "Preparing..." : "Export Excel"}
            </Button>
          </Stack>
        }
      />

      <Box sx={{ height: 3 }}>
        {loading && <LinearProgress />}
      </Box>

      <Box sx={{ maxWidth: showReviewColumns ? 1560 : 1380, mx: "auto", px: 3, py: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
          <Paper
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderLeft: "4px solid #007CBF",
            }}
          >
            {viewMode === "onHold" ? (
              <PauseCircleOutlineIcon sx={{ color: "#007CBF", fontSize: 32 }} />
            ) : (
              <VerifiedUserOutlinedIcon sx={{ color: "#007CBF", fontSize: 32 }} />
            )}
            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {loading ? "—" : totalRecords.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {statLabels[viewMode]}
              </Typography>
            </Box>
          </Paper>
          <Paper
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderLeft: "4px solid #6c757d",
            }}
          >
            <PeopleOutlineIcon sx={{ color: "#6c757d", fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {customerSource}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active data source
              </Typography>
            </Box>
          </Paper>
        </Stack>

        <Paper sx={{ mb: 2.5, overflow: "hidden", borderRadius: 2 }}>
          <Tabs
            value={customerSource}
            onChange={handleSourceChange}
            sx={{
              px: 2,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTabs-indicator": { bgcolor: "#007CBF" },
              "& .MuiTab-root.Mui-selected": { color: "#007CBF", fontWeight: 700 },
            }}
          >
            <Tab label="BRP" value="BRP" />
            <Tab label="SEMINIS" value="SEMINIS" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexShrink={0}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Show
                </Typography>
                <Tabs value={viewMode} onChange={handleViewModeChange} sx={filterTabsSx}>
                  <Tab label="Verified" value="verified" />
                  <Tab label="Non-Verified" value="nonVerified" />
                  <Tab label="On Hold" value="onHold" />
                </Tabs>
              </Stack>

              <TextField
                fullWidth
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                placeholder="Search by Customer ID, shop name, or mobile…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="establishment-type-filter-label">Establishment Type</InputLabel>
                <Select
                  labelId="establishment-type-filter-label"
                  label="Establishment Type"
                  value={establishmentTypeFilter}
                  onChange={(e) => handleEstablishmentTypeFilterChange(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {ESTABLISHMENT_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {viewMode === "nonVerified" && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="kyc-status-filter-label">KYC Status</InputLabel>
                  <Select
                    labelId="kyc-status-filter-label"
                    label="KYC Status"
                    value={kycStatusFilter}
                    onChange={(e) => handleKycStatusFilterChange(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {NON_VERIFIED_KYC_STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {formatKycStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Box>
        </Paper>

        {downloadError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={fetchCustomers}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            "& .MuiTableHead-root": {
              bgcolor: "#f5f7fa",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 700,
              fontSize: "0.78rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.secondary",
              borderBottom: "2px solid",
              borderColor: "divider",
            },
          }}
        >
          {loading && !data ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Establishment Type</TableCell>
                    <TableCell>Shop Name</TableCell>
                    <TableCell>PAN Name</TableCell>
                    <TableCell>Bank Holder</TableCell>
                    {showNameMatchedColumn && <TableCell>Name Matched</TableCell>}
                    <TableCell>Name Match Score</TableCell>
                    <TableCell>Mobile</TableCell>
                    {showReviewColumns && (
                      <>
                        <TableCell>KYC Status</TableCell>
                        <TableCell>Remark</TableCell>
                      </>
                    )}
                    <TableCell sortDirection={lastUpdatedSort === "ASC" ? "asc" : "desc"}>
                      <TableSortLabel
                        active
                        direction={lastUpdatedSort === "ASC" ? "asc" : "desc"}
                        onClick={handleLastUpdatedSort}
                      >
                        Last Updated
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.content?.length ? (
                    data.content.map((row) => (
                      <TableRow
                        key={`${row.customerSource}-${row.customerId}`}
                        hover
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(0,124,191,0.04)" },
                          "&:last-child td": { border: 0 },
                        }}
                        onClick={() => handleRowClick(row.customerId, row.customerSource)}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "#007CBF" }}
                          >
                            {row.customerId}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatEstablishmentType(row.establishmentType)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.shopName || (
                              <Typography component="span" variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.panName || "—"}</TableCell>
                        <TableCell>{row.bankAccountHolderName || "—"}</TableCell>
                        {showNameMatchedColumn && (
                          <TableCell>
                            {row.nameMatched == null ? (
                              "—"
                            ) : (
                              <NameMatchedIcon matched={row.nameMatched} />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {row.userNameMatchScore != null ? (
                            <Typography variant="body2" fontWeight={700}>
                              {row.userNameMatchScore}%
                            </Typography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{row.mobileNumber || "—"}</TableCell>
                        {showReviewColumns && (
                          <>
                            <TableCell>
                              <Chip
                                size="small"
                                color={kycStatusChipColor(row.kycStatus)}
                                label={row.kycStatus?.replace(/_/g, " ")}
                                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.remark || ""} placement="top">
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{ maxWidth: 200, display: "block", cursor: "default" }}
                                >
                                  {row.remark || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                          {formatDate(row.lastUpdatedAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">{emptyMessages[viewMode]}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {data && data.totalElements > 0 && (
                <TablePagination
                  component="div"
                  count={data.totalElements}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                />
              )}
            </>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
}

export default VerifiedKycUsersPage;
